import { ThemedText } from "@/components/themed-text";
import { SheetTextInput } from "@/components/ui/sheet-text-input";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { invalidateBookingQueries } from "@/hooks/queries/use-bookings-query";
import { useDesktopSheetStyle } from "@/hooks/use-desktop-layout";
import { queryKeys } from "@/lib/query-keys";
import bookingService, { Booking } from "@/lib/services/bookings";
import { showToast } from "@/lib/toast";
import { useAuthStore } from "@/stores/use-auth-store";
import { useBookingJobStore } from "@/stores/use-booking-job-store";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "expo-router";
import { useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Keyboard,
  StyleSheet,
  TouchableOpacity,
  View,
  type ViewProps,
} from "react-native";

type CancellationReason = {
  id: string;
  label: string;
};

const OTHER_REASON: CancellationReason = {
  id: "other",
  label: "Other reasons",
};

function withOtherReason(
  reasons: CancellationReason[],
): CancellationReason[] {
  return [...reasons, OTHER_REASON];
}

const INSPECTION_REASONS = withOtherReason([
  {
    id: "dealer_or_garage",
    label: "I'm taking it to a dealer or garage instead",
  },
  { id: "issue_resolved", label: "The issue resolved itself" },
  {
    id: "not_purchasing",
    label: "I've decided not to go ahead with the purchase",
  },
  { id: "reschedule", label: "I need to reschedule my appointment" },
]);

const MISFUEL_REASONS = withOtherReason([
  {
    id: "cover_arranged",
    label: "My breakdown cover or insurer arranged this instead",
  },
  { id: "garage_myself", label: "I'm taking it to a garage myself" },
  {
    id: "wait_and_see",
    label: "I've decided to wait and see if it's drivable",
  },
  { id: "update_location", label: "I need to update my location details" },
]);

const CANCELLATION_REASONS_BY_SERVICE: Record<string, CancellationReason[]> = {
  breakdown_recovery: withOtherReason([
    {
      id: "vehicle_resolved",
      label: "My vehicle no longer needs recovery",
    },
    {
      id: "found_alternative",
      label: "I found another recovery provider",
    },
    {
      id: "breakdown_cover",
      label: "My breakdown cover (AA, RAC, etc.) is handling it instead",
    },
    {
      id: "update_details",
      label: "I need to update my pickup or drop-off details",
    },
  ]),
  accident_recovery: withOtherReason([
    {
      id: "insurer_arranged",
      label:
        "My insurer or accident management company arranged recovery instead",
    },
    { id: "police_arranged", label: "The police arranged recovery" },
    { id: "another_provider", label: "Another provider arrived first" },
    {
      id: "update_details",
      label: "I need to update my pickup or drop-off details",
    },
  ]),
  vehicle_transportation: withOtherReason([
    {
      id: "plans_changed",
      label: "My plans have changed and I no longer need the vehicle moved",
    },
    { id: "cheaper_quote", label: "I found a cheaper quote elsewhere" },
    {
      id: "update_details",
      label: "I need to update the pickup or drop-off details",
    },
    {
      id: "date_time",
      label: "The date or time no longer works for me",
    },
  ]),
  jump_start: withOtherReason([
    { id: "someone_else", label: "Someone else jump-started my vehicle" },
    { id: "started_itself", label: "My vehicle started on its own" },
    {
      id: "bigger_issue",
      label: "It turned out to be a bigger issue than a flat battery",
    },
    { id: "update_location", label: "I need to update my location details" },
  ]),
  pull_out: withOtherReason([
    { id: "someone_else", label: "Someone else pulled my vehicle out" },
    { id: "no_longer_stuck", label: "My vehicle is no longer stuck" },
    { id: "update_location", label: "I need to update my location details" },
    { id: "no_longer_needed", label: "I no longer need this service" },
  ]),
  auto_locksmith: withOtherReason([
    { id: "found_keys", label: "I found my keys" },
    { id: "got_in", label: "I got back into my vehicle myself" },
    { id: "different_locksmith", label: "I used a different locksmith" },
    {
      id: "update_details",
      label: "I need to update my location or appointment time",
    },
  ]),
  mobile_mechanics: withOtherReason([
    {
      id: "garage_instead",
      label: "I'm taking my vehicle to a garage instead",
    },
    {
      id: "less_serious",
      label: "The issue turned out to be less serious than I thought",
    },
    { id: "reschedule", label: "I need to reschedule my appointment" },
    { id: "price", label: "The price doesn't work for me" },
  ]),
  mobile_tyre_fitting: withOtherReason([
    { id: "fitted_spare", label: "I fitted the spare myself" },
    {
      id: "repaired_elsewhere",
      label: "I got it repaired or replaced elsewhere",
    },
    { id: "reschedule", label: "I need to reschedule my appointment" },
    { id: "price", label: "The price doesn't work for me" },
  ]),
  mobile_valet: withOtherReason([
    { id: "plans_changed", label: "My plans have changed" },
    { id: "self_valet", label: "I'm valeting the vehicle myself" },
    { id: "reschedule", label: "I need to reschedule my appointment" },
    { id: "price", label: "The price doesn't work for me" },
  ]),
  mobile_battery_fitting: withOtherReason([
    { id: "jump_start", label: "A jump start got my vehicle running" },
    { id: "fitted_myself", label: "I fitted the battery myself" },
    { id: "reschedule", label: "I need to reschedule my appointment" },
    { id: "price", label: "The price doesn't work for me" },
  ]),
  vehicle_diagnostics: INSPECTION_REASONS,
  vehicle_inspection_diagnostics: INSPECTION_REASONS,
  vehicle_storage: withOtherReason([
    { id: "alternative_storage", label: "I found alternative storage" },
    { id: "sold_or_scrapped", label: "I've sold or scrapped the vehicle" },
    { id: "no_longer_needed", label: "I no longer need storage" },
    {
      id: "update_details",
      label: "I need to update my collection or drop-off details",
    },
  ]),
  out_of_fuel: withOtherReason([
    { id: "someone_brought_fuel", label: "Someone brought fuel to me" },
    { id: "reached_station", label: "I managed to reach a fuel station" },
    { id: "update_location", label: "I need to update my location details" },
    { id: "no_longer_needed", label: "I no longer need this service" },
  ]),
  misfuel: MISFUEL_REASONS,
  adblue_contamination: MISFUEL_REASONS,
};

function getCancellationReasons(serviceType?: string): CancellationReason[] {
  if (!serviceType) {
    return CANCELLATION_REASONS_BY_SERVICE.breakdown_recovery;
  }

  return (
    CANCELLATION_REASONS_BY_SERVICE[serviceType] ??
    CANCELLATION_REASONS_BY_SERVICE.breakdown_recovery
  );
}

export type CancelButtonProps = ViewProps & {
  id: string;
  serviceType?: string;
  style?: ViewProps["style"];
  isDisabled?: boolean;
  title?: string;
  variant?: "default" | "outline";
  showIcon?: boolean;
};

export function CancelButton({
  id,
  serviceType,
  style,
  isDisabled,
  title = "Cancel Booking",
  variant = "default",
  showIcon = true,
}: CancelButtonProps) {
  const qc = useQueryClient();
  const sheetRef = useRef<BottomSheetModal>(null);
  const snapPoints = useMemo(() => ["100%"], []);
  const desktopSheetStyle = useDesktopSheetStyle();
  const [loading, setLoading] = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState("");
  const setActiveBooking = useAuthStore((state) => state.setActiveBooking);
  const { theme } = useAuth();
  const cancellationReasons = useMemo(
    () => getCancellationReasons(serviceType),
    [serviceType],
  );

  const finalReason = useMemo(() => {
    if (!selectedReasonId) {
      return "";
    }

    if (selectedReasonId === "other") {
      return customReason.trim();
    }

    return (
      cancellationReasons.find((reason) => reason.id === selectedReasonId)
        ?.label ?? ""
    );
  }, [cancellationReasons, customReason, selectedReasonId]);

  const canConfirm = finalReason.length > 0;

  const resetSheet = () => {
    setSelectedReasonId(null);
    setCustomReason("");
  };

  const handleClose = () => {
    sheetRef.current?.dismiss();
  };

  const handleSelectReason = (reasonId: string) => {
    setSelectedReasonId(reasonId);

    if (reasonId === "other") {
      setCustomReason("");
      return;
    }

    Keyboard.dismiss();
  };

  const handleOpenSheet = () => {
    setSelectedReasonId(null);
    setCustomReason("");
    sheetRef.current?.present();
  };

  const handleConfirmCancel = async () => {
    if (!canConfirm) {
      showToast.error("Please select a reason for cancellation");
      return;
    }

    try {
      setLoading(true);
      const bookingId = Number(id);
      await bookingService.cancelBooking(bookingId, finalReason);
      useBookingJobStore.getState().clearStore();
      setActiveBooking(null);
      router.setParams({ id: null });
      qc.setQueryData(
        queryKeys.bookings.detail(bookingId),
        (old: Booking | undefined) =>
          old ? { ...old, status: "cancelled" } : old,
      );
      invalidateBookingQueries(qc, bookingId);
      handleClose();
      showToast.success("Booking cancelled successfully");
      router.replace(`/bookings/${bookingId}`);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to cancel booking",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TouchableOpacity
        disabled={loading || isDisabled}
        style={[
          styles.button,
          variant === "outline" && styles.outlineButton,
          style,
          (loading || isDisabled) && { opacity: 0.5 },
        ]}
        onPress={handleOpenSheet}
      >
        {showIcon ? (
          <Ionicons
            name="close-circle"
            size={20}
            color={variant === "outline" ? BrandColors.error : "#fff"}
          />
        ) : null}
        <ThemedText
          style={[styles.text, variant === "outline" && styles.outlineText]}
          type="defaultSemiBold"
        >
          {title}
        </ThemedText>
      </TouchableOpacity>

      <BottomSheetModal
        ref={sheetRef}
        index={0}
        snapPoints={snapPoints}
        style={desktopSheetStyle}
        enablePanDownToClose
        onDismiss={resetSheet}
        backgroundStyle={{ backgroundColor: theme.background }}
        handleIndicatorStyle={styles.handle}
      >
        <BottomSheetScrollView
          contentContainerStyle={styles.sheetContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.sheetPrompt, { borderColor: theme.border }]}>
            <Ionicons
              name="alert-circle-outline"
              size={20}
              color={BrandColors.mediumGray}
            />
            <ThemedText style={styles.sheetPromptText} type="defaultSemiBold">
              Why are you canceling?
            </ThemedText>
          </View>

          <View style={styles.reasonList}>
            {cancellationReasons.map((reason) => {
              const isSelected = selectedReasonId === reason.id;

              return (
                <TouchableOpacity
                  key={reason.id}
                  style={[
                    styles.reasonOption,
                    { borderColor: theme.border },
                    isSelected && styles.reasonOptionSelected,
                  ]}
                  onPress={() => handleSelectReason(reason.id)}
                  activeOpacity={0.8}
                >
                  <ThemedText
                    style={[isSelected && styles.reasonOptionTextSelected]}
                    type="small"
                  >
                    {reason.label}
                  </ThemedText>
                </TouchableOpacity>
              );
            })}
          </View>

          {selectedReasonId === "other" ? (
            <View style={styles.customReasonGroup}>
              <ThemedText type="smallSemiBold" style={styles.customReasonLabel}>
                Tell us more
              </ThemedText>
              <SheetTextInput
                placeholder="Please describe your reason..."
                placeholderTextColor={BrandColors.mediumGray}
                value={customReason}
                onChangeText={setCustomReason}
                multiline
                maxLength={500}
                autoFocus
                style={[
                  styles.customReasonInput,
                  {
                    borderColor: theme.border,
                    color: theme.text,
                    backgroundColor: theme.background,
                  },
                ]}
              />
            </View>
          ) : null}

          <View style={styles.sheetButtons}>
            <TouchableOpacity
              style={[
                styles.sheetButton,
                styles.confirmButton,
                !canConfirm && styles.confirmButtonDisabled,
                loading && styles.disabledButton,
              ]}
              onPress={handleConfirmCancel}
              disabled={loading || !canConfirm}
            >
              {loading ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <ThemedText
                  style={[
                    styles.confirmButtonText,
                    !canConfirm && styles.confirmButtonTextDisabled,
                  ]}
                  type="defaultSemiBold"
                >
                  Cancel Booking
                </ThemedText>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.sheetButton,
                styles.declineButton,
                { borderColor: theme.border },
              ]}
              onPress={handleClose}
              disabled={loading}
            >
              <ThemedText
                style={styles.declineButtonText}
                type="defaultSemiBold"
              >
                Decline
              </ThemedText>
            </TouchableOpacity>
          </View>
        </BottomSheetScrollView>
      </BottomSheetModal>
    </>
  );
}

const styles = StyleSheet.create({
  text: {
    fontWeight: "600",
    color: "#fff",
  },
  button: {
    height: 50,
    width: "100%",
    borderRadius: 14,
    backgroundColor: "#FF3B30",
    borderColor: "#FF3B30",
    overflow: "hidden",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  outlineButton: {
    backgroundColor: BrandColors.white,
    borderWidth: 1.5,
    borderColor: BrandColors.error,
  },
  outlineText: {
    color: BrandColors.error,
  },
  handle: {
    backgroundColor: "#334155",
  },
  sheetContent: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 40,
    gap: 16,
  },
  sheetPrompt: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: BrandColors.backgroundGray,
  },
  sheetPromptText: {
    flex: 1,
    color: BrandColors.charcoal,
  },
  reasonList: {
    gap: 0,
  },
  reasonOption: {
    paddingVertical: 16,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
  },
  reasonOptionSelected: {
    backgroundColor: BrandColors.errorLight,
    borderRadius: 10,
    borderBottomWidth: 1,
    marginHorizontal: -4,
    paddingHorizontal: 8,
  },
  reasonOptionTextSelected: {
    color: BrandColors.error,
    fontWeight: "600",
  },
  customReasonGroup: {
    gap: 8,
  },
  customReasonLabel: {
    color: BrandColors.charcoal,
  },
  customReasonInput: {
    minHeight: 100,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    textAlignVertical: "top",
  },
  sheetButtons: {
    flexDirection: "row",
    gap: 12,
    marginTop: 4,
  },
  sheetButton: {
    flex: 1,
    minHeight: 48,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  confirmButton: {
    backgroundColor: BrandColors.error,
  },
  confirmButtonDisabled: {
    backgroundColor: BrandColors.lightGray,
  },
  confirmButtonText: {
    color: "#fff",
    fontWeight: "600",
    textAlign: "center",
  },
  confirmButtonTextDisabled: {
    color: BrandColors.white,
  },
  declineButton: {
    backgroundColor: BrandColors.white,
    borderWidth: 1,
  },
  declineButtonText: {
    color: BrandColors.mediumGray,
    fontWeight: "600",
  },
  disabledButton: {
    opacity: 0.6,
  },
});
